import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { mergeMap, map, catchError, tap } from "rxjs/operators";

import { ExportTemplatesActions } from "./export-templates.actions";
import { of } from "rxjs";
import { ExportTemplateMapper } from "../../../mappers/table/export-templates/export-template-mapper";
import { Store } from "@ngrx/store";
import { FeedbackActions } from "../../common/feedback/feedback.actions";
import { LoaderActions } from "../../common/loader/loader.actions";
import { TranslateService } from "@ngx-translate/core";
import {FileService, NavigationService} from '../../../shared/services';
import { ExportModelRepo } from "../../../repositories/private";
import {downloadFileOnLocal} from '../../../shared/utils';
import {
  DEFAULT_PAGE_SIZE,
  EXPORT_TEMPLATE_SAMPLE_FILE_NAME,
  EXPORT_TEMPLATE_SAMPLE_FILE_PATH
} from '../../../shared/constants';


export class ExportTemplatesEffect {
   private readonly actions$ = inject(Actions);
    private readonly exportTemplateMapper = inject( ExportTemplateMapper);
    private readonly translate = inject( TranslateService);
    private readonly store = inject(Store);
    private readonly exportModelRepo = inject( ExportModelRepo);
    private readonly navigationService = inject(NavigationService);
    private readonly fileService = inject(FileService);

  searchModels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportTemplatesActions.search),
      tap(() => this.startLoader()),
      mergeMap((param) => {
        return this.exportModelRepo.searchExportModels(param.filter).pipe(
          map((response) => ({
            paging: response.paging,
            list: this.exportTemplateMapper.mapEntities(response.list ?? []),
          })),
          map((response) => ExportTemplatesActions.fetchSuccess({ response })),
          tap(() => this.stopLoader()),
          catchError((error) =>
            of(
              ExportTemplatesActions.fetchFailure({
                message: error?.toString(),
              })
            )
          )
        );
      })
    );
  });

  createModel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportTemplatesActions.createModel),
      tap(() => this.startLoader()),
      mergeMap((param) => {
        return this.exportModelRepo
          .createModel({
            name: param.modelName,
            file: param.file,
          })
          .pipe(
            map(() =>
              ExportTemplatesActions.createModelSuccess({ message: "" })
            ),
            tap(() =>
              this.afterCreateTemplateSuccess(
                this.translate.instant(
                  "feedback.detail.template-create-success"
                )
              )
            ),
            catchError((error) => {
              const errorAction = of(
                ExportTemplatesActions.createModelFailure({
                  message: error?.toString(),
                })
              );
              this.afterCreateTemplateFailure(
                this.translate.instant(
                  "feedback.detail.template-create-failure"
                )
              );

              return errorAction;
            })
          );
      })
    );
  });

   /**
     * Download an Export Model
     */
    downloadExportModel$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(ExportTemplatesActions.downloadModel),
        mergeMap((param) =>
          this.exportModelRepo
            .downloadExportModel(param.modelUid)
            .pipe(
              map((response) => {
                downloadFileOnLocal(response);

                return  ExportTemplatesActions.downloadModelSuccess({
                    message: this.translate.instant('feedback.detail.download-success')
                });
              }),
              catchError((error) => {
                return of(ExportTemplatesActions.downloadModelFailure({
                  message: error?.toString()
                }));

              })
            )
        )
      );
    });

    /**
     * Download the sample template file
     */
    downloadSampleExportModel$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(ExportTemplatesActions.downloadSampleModel),
        mergeMap(() =>
          this.fileService
            .get(EXPORT_TEMPLATE_SAMPLE_FILE_PATH)
            .pipe(
              map((response) => {
                downloadFileOnLocal({blob: response, fileName: EXPORT_TEMPLATE_SAMPLE_FILE_NAME});
                return  ExportTemplatesActions.downloadSampleModelSuccess({
                    message: this.translate.instant('feedback.detail.download-success')
                });
              }),
              catchError((error) => {
                return of(ExportTemplatesActions.downloadSampleModelFailure({
                  message: error?.toString()
                }));

              })
            )
        )
      );
    });

  /**
    * Delete a model
  */
  deleteModel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ExportTemplatesActions.deleteModel),
      tap(() => this.startLoader()),
      mergeMap((param) => {
        return this.exportModelRepo.delete(param.modelUid)
          .pipe(
            map(() =>
              ExportTemplatesActions.deleteModelSuccess({ message: "" })
            ),
            tap(() =>
              this.afterCreateTemplateSuccess(
                this.translate.instant(
                  "feedback.detail.template-delete-success"
                )
              )
            ),
            catchError((error) => {
              const errorAction = of(
                ExportTemplatesActions.deleteModelFailure({
                  message: error?.toString(),
                })
              );
              this.afterCreateTemplateFailure(
                this.translate.instant(
                  "feedback.detail.template-delete-failure"
                )
              );

              return errorAction;
            })
          );
      })
    );
  });

  private startLoader() {
    this.store.dispatch(
      LoaderActions.start({
        message: this.translate.instant("loader.default-title"),
      })
    );
  }

  private stopLoader() {
    this.store.dispatch(LoaderActions.stop());
  }

  private afterCreateTemplateSuccess(detail: string) {
    this.stopLoader();
    this.store.dispatch(FeedbackActions.success({ message: detail }));
    this.navigationService.gotoExportTemplatePage();
    this.refresh();
  }
  private afterCreateTemplateFailure(detail: string) {
    this.stopLoader();
    this.store.dispatch(FeedbackActions.failure({ message: detail }));
    this.refresh();
  }

  private refresh() {
    this.store.dispatch(
      ExportTemplatesActions.search({
        currentPage: 0,
        filter: { offset: 0, limit: DEFAULT_PAGE_SIZE },
      })
    );
  }
}
